import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const TIMEZONES = [
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Darwin',
  'Australia/Hobart',
];

export default function RegisterPage() {
  const { loginOwner } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    slug: '',
    email: '',
    password: '',
    confirmPassword: '',
    location: '',
    timezone: 'Australia/Sydney',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      // Auto-generate slug from name
      if (name === 'name') {
        next.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      }
      return next;
    });
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.slug || !form.email || !form.password) {
      setError('All required fields must be filled.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!/^[a-z0-9-]+$/.test(form.slug)) {
      setError('Restaurant code may only contain lowercase letters, numbers and hyphens.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/owner/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          slug: form.slug,
          email: form.email,
          password: form.password,
          location: form.location || undefined,
          timezone: form.timezone,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Registration failed.'); return; }
      loginOwner(data.token, data.business);
      navigate('/dashboard');
    } catch {
      setError('Network error. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="main-full">
      <div className="auth-container" style={{ maxWidth: 500 }}>
        <div className="auth-card">
          <div className="auth-logo">
            <h1>Roster<span>AI</span></h1>
            <p>Smart restaurant staff scheduling</p>
          </div>

          <h2 className="auth-title">Register Your Restaurant</h2>
          <p className="auth-subtitle">Set up your free account</p>

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Restaurant Name <span className="required">*</span></label>
              <input
                type="text"
                className="form-control"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. The Sushi Place"
                required
              />
            </div>

            <div className="form-group">
              <label>Restaurant Code <span className="required">*</span></label>
              <input
                type="text"
                className="form-control"
                name="slug"
                value={form.slug}
                onChange={handleChange}
                placeholder="e.g. the-sushi-place"
                required
              />
              <div className="form-hint">Auto-generated from your restaurant name. Staff use this to log in. You can customise it.</div>
            </div>

            <div className="form-group">
              <label>Owner Email <span className="required">*</span></label>
              <input
                type="email"
                className="form-control"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="owner@restaurant.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Password <span className="required">*</span></label>
                <input
                  type="password"
                  className="form-control"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Min 8 characters"
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="form-group">
                <label>Confirm Password <span className="required">*</span></label>
                <input
                  type="password"
                  className="form-control"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  placeholder="Repeat password"
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Location (suburb / city)</label>
              <input
                type="text"
                className="form-control"
                name="location"
                value={form.location}
                onChange={handleChange}
                placeholder="e.g. Surry Hills, Sydney NSW"
              />
              <div className="form-hint">Used for weather and event-based roster adjustments</div>
            </div>

            <div className="form-group">
              <label>Timezone</label>
              <select className="form-control" name="timezone" value={form.timezone} onChange={handleChange}>
                {TIMEZONES.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>

            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={loading} style={{ marginTop: 8 }}>
              {loading ? <span className="spinner" /> : 'Create Account'}
            </button>
          </form>

          <div className="auth-footer">
            Already have an account? <Link to="/login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
